/**
 * ============================================================
 * PGLP | MONTE HERMOM
 * SERVICE: AmostrasService
 * ------------------------------------------------------------
 * Ingestão de amostras de mercado (NBR 14653-2).
 * Zero mock. Zero fallback silencioso.
 *
 * REGRA:
 *   - Se a API responde, retorna dados.
 *   - Se falha de rede, propaga erro marcado (_enfileirado).
 *   - Se retorno vazio, retorna array vazio (não inventa dado).
 *
 * VERSÃO: 2.0.0
 * ============================================================
 */

import { laudoApi } from './laudoApi';

/* =========================
   VALIDAÇÃO DE AMOSTRA
========================= */

/**
 * Valida uma amostra contra regras mínimas da NBR 14653-2.
 * Retorna lista de problemas. Vazio = amostra íntegra.
 */
function validarAmostra(amostra) {
  const erros = [];

  if (!amostra?.id) erros.push('id ausente');
  if (!amostra?.valor && amostra?.valor !== 0) erros.push('valor ausente');
  if (!amostra?.area && amostra?.area !== 0) erros.push('area ausente');
  if (!amostra?.data) erros.push('data ausente');
  if (!amostra?.fonte) erros.push('fonte ausente');

  return erros;
}

/**
 * Normaliza uma amostra vinda do backend para o formato esperado
 * pelo motor estatístico.
 */
function normalizarAmostra(a) {
  return {
    id: String(a.id),
    tipo: a.tipo || 'TRANSAÇÃO',
    valor: Number(a.valor) || 0,
    area: Number(a.area) || 0,
    data: a.data || null,
    fonte: a.fonte || 'Não informada',
    coordenadas: a.coordenadas || null,
    fatorLocalizacao: Number(a.fatorLocalizacao) || 1.0,

    // Preserva o original para auditoria
    _raw: a,
  };
}

/* =========================
   BUSCA PRINCIPAL
========================= */

/**
 * Busca amostras de mercado próximas a uma coordenada.
 *
 * @param {number} latitude
 * @param {number} longitude
 * @param {number} raioKm          — raio de busca (default: 1.5km)
 * @param {object} opcoes
 * @param {string} opcoes.reportNumber
 * @param {string} opcoes.perito
 *
 * @returns {Promise<{
 *   amostras: Array,
 *   total: number,
 *   raioKm: number,
 *   centro: { lat, lng },
 *   avisos: string[],
 * }>}
 */
export async function fetchAmostrasReais(
  latitude,
  longitude,
  raioKm = 1.5,
  opcoes = {}
) {
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    throw new Error(
      'fetchAmostrasReais: latitude e longitude devem ser números válidos'
    );
  }

  if (!Number.isFinite(raioKm) || raioKm <= 0) {
    throw new Error('fetchAmostrasReais: raioKm deve ser maior que zero');
  }

  const avisos = [];
  let resposta;

  try {
    resposta = await laudoApi.buscarAmostras(latitude, longitude, raioKm);
  } catch (err) {
    // Propaga erro com marca de fila se aplicável
    if (err._enfileirado) {
      throw Object.assign(
        new Error('Sem conexão — busca de amostras indisponível offline.'),
        { _enfileirado: true, _original: err }
      );
    }
    throw new Error(
      `Falha na busca de amostras: ${err.message || 'erro desconhecido'}`
    );
  }

  // Backend pode retornar array direto ou { data: [...] }
  const lista = Array.isArray(resposta)
    ? resposta
    : Array.isArray(resposta?.data)
      ? resposta.data
      : [];

  if (lista.length === 0) {
    avisos.push(
      `Nenhuma amostra encontrada no raio de ${raioKm}km. ` +
      `Considere ampliar a área ou cadastrar manualmente.`
    );
  }

  // Filtra amostras inválidas, mantendo rastro dos motivos
  const validas = [];
  const invalidas = [];

  for (const item of lista) {
    const erros = validarAmostra(item);
    if (erros.length === 0) {
      validas.push(normalizarAmostra(item));
    } else {
      invalidas.push({ item, erros });
    }
  }

  if (invalidas.length > 0) {
    avisos.push(
      `${invalidas.length} amostra(s) descartada(s) por dados incompletos.`
    );
  }

  return {
    amostras: validas,
    total: validas.length,
    raioKm,
    centro: { lat: latitude, lng: longitude },
    avisos,
    _invalidas: invalidas, // para debug/curadoria
  };
}

/* =========================
   CÁLCULOS ESTATÍSTICOS
========================= */

/**
 * Aplica homogeneização sobre uma lista de amostras.
 * V_h = V_b × (1 + i)
 */
export function homogeneizar(amostras = []) {
  return amostras.map(a => ({
    ...a,
    valorHomogeneizado: +(a.valor * (1 + a.fatorLocalizacao - 1)).toFixed(2),
    valorUnitario: a.area > 0 ? +(a.valor / a.area).toFixed(2) : 0,
  }));
}

/**
 * Saneamento estatístico NBR 14653-2 (elimina outliers por desvio-padrão).
 *
 * @param {Array} amostras        — lista já homogeneizada
 * @param {number} limite         — multiplicador do desvio-padrão (default: 2)
 * @returns {{
 *   amostrasSaneadas: Array,
 *   descartadas: Array,
 *   media: number,
 *   desvioPadrao: number,
 *   mediana: number,
 *   limiteAplicado: number,
 * }}
 */
export function sanearAmostras(amostras = [], limite = 2) {
  if (amostras.length === 0) {
    return {
      amostrasSaneadas: [],
      descartadas: [],
      media: 0,
      desvioPadrao: 0,
      mediana: 0,
      limiteAplicado: limite,
    };
  }

  const valores = amostras
    .map(a => a.valorUnitario)
    .filter(v => Number.isFinite(v) && v > 0);

  if (valores.length === 0) {
    return {
      amostrasSaneadas: amostras,
      descartadas: [],
      media: 0,
      desvioPadrao: 0,
      mediana: 0,
      limiteAplicado: limite,
    };
  }

  const media = valores.reduce((a, b) => a + b, 0) / valores.length;
  const variancia = valores.reduce((s, v) => s + Math.pow(v - media, 2), 0) / valores.length;
  const desvioPadrao = Math.sqrt(variancia);

  // Mediana
  const ordenados = [...valores].sort((a, b) => a - b);
  const meio = Math.floor(ordenados.length / 2);
  const mediana = ordenados.length % 2 === 0
    ? (ordenados[meio - 1] + ordenados[meio]) / 2
    : ordenados[meio];

  // Saneamento
  const limiteInferior = media - limite * desvioPadrao;
  const limiteSuperior = media + limite * desvioPadrao;

  const amostrasSaneadas = [];
  const descartadas = [];

  for (const a of amostras) {
    const v = a.valorUnitario;
    if (!Number.isFinite(v) || v <= 0) {
      descartadas.push({ ...a, motivo: 'valor unitário inválido' });
    } else if (v < limiteInferior || v > limiteSuperior) {
      descartadas.push({ ...a, motivo: 'outlier (fora de ±' + limite + 'σ)' });
    } else {
      amostrasSaneadas.push(a);
    }
  }

  return {
    amostrasSaneadas,
    descartadas,
    media: +media.toFixed(2),
    desvioPadrao: +desvioPadrao.toFixed(2),
    mediana: +mediana.toFixed(2),
    limiteAplicado: limite,
    intervalo: {
      inferior: +limiteInferior.toFixed(2),
      superior: +limiteSuperior.toFixed(2),
    },
  };
}

/* =========================
   EXPORT DEFAULT (compatibilidade)
========================= */

const AmostrasService = {
  fetchAmostrasReais,
  homogeneizar,
  sanearAmostras,
};

export default AmostrasService;